import { AfterViewInit, Component, EventEmitter, Inject, Input, LOCALE_ID, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TourInfo, InfoSectionType, Product, Media, MediaType, InfoActionType, TourInfoSection, LanguageMap, Mote, MotesPaths, MoteType, MoteTranslation } from '../../models';
import { AutoSub, AutoSubOnDestroy, ProductService, ScrollToElementService, DeviceService } from '../../providers';

@AutoSubOnDestroy()
@Component({
    selector: 'tour-info',
    templateUrl: './tour-info.component.html',
    styleUrls: ['./tour-info.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class TourInfoComponent implements OnInit, AfterViewInit {

    @Input('tourInfo') set setTour(tourInfo: TourInfo) {
        this.init(tourInfo);
    }

    @Input() translations: MoteTranslation[] = [];
    @Input() tour: Mote;
    @Input() hideTourMap: boolean = false;

    // While true, a 'start tour' click will open drawer or qr-drop-down. Otherwise the startTour event is emitted immediately.
    @Input() startTourPopupsEnabled: boolean = true;

    @Output() playMedia = new EventEmitter<Media>();
    @Output() buyNow = new EventEmitter<Product>();
    @Output() startTour = new EventEmitter<boolean>();
    @Output() openTranslation = new EventEmitter<string>();

    public tourInfo: TourInfo = null;
    public products: Product[] = [];
    public firstImageLoaded: boolean = false;
    public isMobile: boolean = false;
    public qrUrl: string = '';
    public openDropDown: boolean = false;
    public openStartTourDrawer: boolean = false;

    // Expose to view
    public InfoSectionType = InfoSectionType;
    public MediaType = MediaType;
    public LanguageMap = LanguageMap;
    public MoteType = MoteType;
    public InfoActionType = InfoActionType;

    constructor(private productService: ProductService,
        private scroll: ScrollToElementService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private deviceService: DeviceService,
        @Inject(LOCALE_ID) public locale: string) { }

    ngOnInit(): void { }

    ngAfterViewInit(): void {
        if(!this.startTourPopupsEnabled) return;

        // Detect startTour query
        this.openStartTourDrawer = this.activatedRoute.snapshot.queryParamMap.get('startTour') == 'true';

        // Create startTour query and include previewKey if applicable
        const previewKey = this.activatedRoute.snapshot.queryParamMap.get('previewKey');
        const previewQuery = previewKey ? `&previewKey=${previewKey}` : '';
        this.qrUrl = `${MotesPaths.MOTES_HOME}/${this.locale}/tours/${this.tourInfo?.key}?startTour=true${previewQuery}`;
    }

    private init(tourInfo: TourInfo) {
        this.tourInfo = tourInfo;
        if(!tourInfo) return;

        this.isMobile = this.deviceService.isMobile();
        let productKeys = Object.keys(tourInfo.products);

        AutoSub(this).reg['tourProductSub'] = this.productService.watchProducts('', productKeys).subscribe(products => {
            this.products = products.sort((a, b) => a.price - b.price);
        });
    }

    public mediaClick(url: string) {
        if(!url) return;
        // Preserve query params for tour previews
        this.router.navigate([url], { queryParamsHandling: "preserve" });
    }

    public emitAction(actionType: InfoActionType, section: TourInfoSection, product?: Product) {
        switch(actionType) {
            case InfoActionType.SHOW_TRAILER:
                this.playMedia.emit(section.media);
                break;

            case InfoActionType.BUY:
                this.buyNow.emit(product);
                break;

            case InfoActionType.START_TOUR:
                if(this.startTourPopupsEnabled) {
                    if(this.isMobile) this.openStartTourDrawer = true;
                    else this.openDropDown = true;
                } else {
                    this.startTour.emit();
                }
                break;

            case InfoActionType.SCROLL_TO_SNEAK_PEEK:
                this.scroll.scrollToId(this.tour.key + '_sectionE', { behavior: "smooth", block: "center", inline: "nearest" });
                break;

            case InfoActionType.SCROLL_TO_PRODUCTS:
                this.scroll.scrollToId(this.tour.key + '_sectionG', { behavior: "smooth", block: "start", inline: "nearest" });
                break;

            case InfoActionType.OPEN_MAP:
                // Preserve query params for tour previews
                this.router.navigate([MotesPaths.STORY_MAP(this.tourInfo.tourKey)], { queryParamsHandling: "preserve" });
                break;
        }
    }

    public trackBySectionKey(idx, section: TourInfoSection) {
        return section.key;
    }

    public redirectToTranslation(translationKey: string) {
        this.openTranslation.emit(translationKey);
    }

    public openTour(inBrowser: boolean) {
        this.startTour.emit(inBrowser);
        this.openStartTourDrawer = false;
    }
}
